/**
 * Policy Drift View
 *
 * Surfaces staleness findings: policies whose cited regulations changed,
 * are superseded, new related regulations emerged, or are overdue for review.
 */
import React, { useState, useEffect, useCallback } from 'react';
import InfoTooltip from '../../components/InfoTooltip';
import NotesModal from './NotesModal';
import {
  PencilSquareIcon,
  ArrowPathIcon,
  DocumentPlusIcon,
  ClockIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const API = process.env.REACT_APP_API_URL;

const SEVERITY_CFG = {
  critical: { label: 'Critical', color: 'bg-red-100 text-red-800 border-red-300' },
  high:     { label: 'High',     color: 'bg-red-50 text-red-700 border-red-200' },
  medium:   { label: 'Medium',   color: 'bg-amber-50 text-amber-700 border-amber-200' },
  low:      { label: 'Low',      color: 'bg-slate-50 text-slate-600 border-slate-200' },
};

const TYPE_CFG = {
  cfr_content:         { label: 'CFR amended',   Icon: PencilSquareIcon },
  supersession:        { label: 'Superseded',    Icon: ArrowPathIcon },
  new_related_mandate: { label: 'New related',   Icon: DocumentPlusIcon },
  review_overdue:      { label: 'Review overdue', Icon: ClockIcon },
};

const STATUS_CFG = {
  open:         { label: 'Open',         color: 'bg-red-50 text-red-700 border-red-200' },
  acknowledged: { label: 'Acknowledged', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  resolved:     { label: 'Resolved',     color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  dismissed:    { label: 'Dismissed',    color: 'bg-slate-100 text-slate-500 border-slate-200' },
};

export default function DriftView({ onOpenPolicy }) {
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg] = useState('');
  const [latestRun, setLatestRun] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [notesModal, setNotesModal] = useState(null); // { id, status, title, description, confirmLabel, tone }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set('status', statusFilter);
      const [fRes, rRes] = await Promise.all([
        fetch(`${API}/api/ppm/drift?${qs}`, { credentials: 'include' }),
        fetch(`${API}/api/ppm/drift/runs/latest`, { credentials: 'include' }),
      ]);
      const fData = await fRes.json();
      const rData = await rRes.json();
      if (fData.success) setFindings(fData.findings || []);
      if (rData.success) setLatestRun(rData.run || null);
    } catch {}
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const triggerScan = async () => {
    setScanning(true); setScanMsg('');
    try {
      const res = await fetch(`${API}/api/ppm/drift/scan`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      setScanMsg(data.success ? 'Drift scan started — refresh in a moment' : (data.message || 'Failed'));
      setTimeout(load, 10000);
    } catch { setScanMsg('Scan failed'); }
    setScanning(false);
  };

  const updateStatus = async (id, status, notes) => {
    try {
      await fetch(`${API}/api/ppm/drift/${id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, resolution_notes: notes || null }),
      });
      load();
    } catch {}
  };

  const counts = findings.reduce((acc, f) => { acc[f.severity] = (acc[f.severity] || 0) + 1; return acc; }, {});

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-slate-800 tracking-tight">Outdated Policy Alert</h2>
              <InfoTooltip
                title="Outdated Policy Alert"
                width={380}
                content={
                  <>
                    Flags policies whose <span className="text-white font-medium">underlying regulation has changed</span> since
                    they were approved — CFR amendments, supersession, new related rules, or
                    just being overdue for review. Most CMS enforcement actions cite a policy
                    that didn't keep up with a rule change.
                  </>
                }
              />
            </div>
            <p className="text-slate-400 text-xs mt-0.5">
              Staleness signals — policies whose regulatory basis has shifted, or that are overdue for review.
              {findings.length > 0 && ` · ${findings.length} findings${counts.high ? ' (' + counts.high + ' high)' : ''}`}
            </p>
          </div>
          <button onClick={triggerScan} disabled={scanning}
            className="px-5 py-2.5 rounded-xl bg-slate-800 text-white text-sm font-semibold hover:bg-slate-900 disabled:opacity-50 transition-colors">
            {scanning ? 'Starting...' : 'Run Drift Scan'}
          </button>
        </div>

        {scanMsg && (
          <div className="mb-4 p-3 rounded-xl bg-orange-50 border border-orange-200 text-orange-700 text-xs">{scanMsg}</div>
        )}

        {latestRun && latestRun.finished_at && (
          <div className="mb-4 text-xs text-slate-400">
            Last scan: {new Date(latestRun.started_at).toLocaleString()} · {latestRun.policies_scanned} policies · {latestRun.findings_created} findings
          </div>
        )}

        {/* Status filter */}
        <div className="flex gap-2 mb-4 flex-wrap">
          <div className="flex gap-1 p-1 bg-slate-100/80 rounded-xl">
            {['open', 'acknowledged', 'resolved', 'dismissed', 'all'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s === 'all' ? '' : s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all capitalize ${
                  (statusFilter || 'all') === s ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}>{s}</button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="space-y-3">
            {[1, 2].map(i => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 animate-pulse">
                <div className="h-4 bg-slate-100 rounded w-1/2 mb-2" />
                <div className="h-3 bg-slate-100 rounded w-1/3" />
              </div>
            ))}
          </div>
        )}

        {!loading && findings.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl flex items-center justify-center text-emerald-600">
              <CheckCircleIcon className="w-8 h-8" strokeWidth={1.75} />
            </div>
            <h3 className="text-base font-semibold text-slate-700 mb-1">No drift detected</h3>
            <p className="text-sm text-slate-400">{statusFilter === 'open' ? 'All policies appear aligned with their cited regulations and up to date.' : 'No findings match this filter.'}</p>
          </div>
        )}

        {!loading && findings.length > 0 && (
          <div className="space-y-3">
            {findings.map(f => {
              const sev = SEVERITY_CFG[f.severity] || SEVERITY_CFG.medium;
              const typ = TYPE_CFG[f.drift_type] || { label: f.drift_type, Icon: null };
              const stat = STATUS_CFG[f.status] || STATUS_CFG.open;
              const isOpen = expanded[f.id];
              return (
                <div key={f.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                  <button onClick={() => setExpanded(e => ({ ...e, [f.id]: !e[f.id] }))}
                    className="w-full text-left p-5 hover:bg-slate-50/50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${sev.color}`}>{sev.label}</span>
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-50 text-slate-600 border border-slate-200">
                            {typ.Icon && <typ.Icon className="w-3.5 h-3.5" strokeWidth={1.75} />} {typ.label}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${stat.color}`}>{stat.label}</span>
                          <button onClick={(e) => { e.stopPropagation(); onOpenPolicy(f.policy_id); }}
                            className="text-xs text-orange-700 hover:underline font-mono">
                            {f.policy_number}
                          </button>
                        </div>
                        <p className="text-sm text-slate-700 font-medium">{f.summary}</p>
                      </div>
                      <svg className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-slate-100 p-5 space-y-3">
                      {f.detail && <p className="text-sm text-slate-700 leading-relaxed">{f.detail}</p>}

                      {(f.old_value || f.new_value) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {f.old_value && (
                            <div className="rounded-xl border border-slate-200 bg-red-50/20 p-3">
                              <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">At approval</div>
                              <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">{f.old_value}</p>
                            </div>
                          )}
                          {f.new_value && (
                            <div className="rounded-xl border border-slate-200 bg-emerald-50/20 p-3">
                              <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Current</div>
                              <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">{f.new_value}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {f.recommendation_id && f.status === 'open' && (
                        <div className="text-xs text-slate-500">A recommendation has been created in the Inbox.</div>
                      )}

                      {f.resolution_notes && (
                        <div className="text-xs text-slate-500 italic">Notes: {f.resolution_notes}</div>
                      )}

                      {f.status === 'open' && (
                        <div className="flex gap-2 pt-2">
                          <button onClick={() => updateStatus(f.id, 'acknowledged')}
                            className="px-4 py-2 rounded-xl bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 transition-colors">
                            Acknowledge
                          </button>
                          <button onClick={() => setNotesModal({
                              id: f.id, status: 'resolved', tone: 'emerald',
                              title: 'Mark finding resolved',
                              description: `${f.policy_number}: ${f.summary}`,
                              placeholder: 'What was done? (e.g. policy updated to the amended regulation, review completed)',
                              confirmLabel: 'Mark Resolved',
                            })}
                            className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors">
                            Mark Resolved
                          </button>
                          <button onClick={() => setNotesModal({
                              id: f.id, status: 'dismissed', tone: 'orange',
                              title: 'Dismiss finding',
                              description: `${f.policy_number}: ${f.summary}`,
                              placeholder: 'Why does this finding not require action?',
                              confirmLabel: 'Dismiss',
                            })}
                            className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-xs font-medium hover:bg-slate-50 transition-colors">
                            Dismiss
                          </button>
                        </div>
                      )}

                      {f.status !== 'open' && (
                        <button onClick={() => updateStatus(f.id, 'open', null)}
                          className="text-xs text-slate-500 hover:text-slate-700 font-medium">
                          Reopen
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <NotesModal
          open={!!notesModal}
          title={notesModal?.title}
          description={notesModal?.description}
          placeholder={notesModal?.placeholder}
          confirmLabel={notesModal?.confirmLabel}
          tone={notesModal?.tone}
          onClose={() => setNotesModal(null)}
          onConfirm={(notes) => { updateStatus(notesModal.id, notesModal.status, notes); setNotesModal(null); }}
        />
      </div>
    </div>
  );
}
